import { Ref, ref, watch, computed, UnwrapRef } from '@vue/composition-api';
import { useSessionId } from './useSessionId';

/**
 * ベンダーに提供されてるものであるから、FraudAlertから提供されたスクリプトは改変しないでそのまま配置する方針。たとえ、気になる部分があっても。
 *
 * ツッコミどころとしては
 *
 * - asyncとdeferは排他なのに両方セットされてる件、
 * - 第二引数が文字列の数だったり数値型の数だったりする件、
 * - _cpaq.pushが即時関数と外側の両方に書いてある件、
 * - 即時実行関数作ってる意図は間違いなく、グローバル空間に変数が漏れないようにするためのはずだけど、ct1が不用意に漏れちゃってる件、
 *
 * などある。
 * グローバル空間に展開した変数はアプリのwebview経由なんかで比較的容易に盗看取ることが可能なのだけど、
 * ct1はcookieなのでもともと容易く抜けるし、_cpaqもそれほどやばい情報でも無いためまぁ良いか、という感じ。
 * どのみち、iframe内でcookie絡みの動作は完全に死ぬし。
 *
 * 何かあって変更する場合ここを修正すればいいかも。
 * ちなみに文字列の前に javascriptってコメント入れてるのは、
 * 文字列の内容にコードハイライトを効かせるvscode拡張用。必要に応じて入れてね。
 * @see https://marketplace.visualstudio.com/items?itemName=HarryDuong.comment-tagged-templates-string
 */
const FRAUD_ALERT_SCRIPT_TEMPLATE = /* javascript */ `
  var ct1 = ('; '+document.cookie).split('; ct1=').pop().split(';')[0];
  var _cpaq = _cpaq || [];
  _cpaq.push(['setCustomVariable','1','sessionId','●●●●●●●●●●',"visit"]);
  _cpaq.push(['setCustomVariable', 2, 'ct1', ct1, 'visit']);
  _cpaq.push(['setCustomVariable', 3, 'cookieDomain', '.XXX.co.jp', 'visit']);
  _cpaq.push(['trackPageView']);
  _cpaq.push(['enableLinkTracking']);
  (function() {
      var u="https://p.fraud-alert.net/";
      var u2="https://static.fraud-alert.net/";
      _cpaq.push(['setTrackerUrl', u+'accept']);
      _cpaq.push(['setSiteId', ▲▲▲▲▲▲▲▲▲▲]);
      var d=document, g=d.createElement('script'), s=d.getElementsByTagName('script')[0];
      g.type='text/javascript'; g.async=true; g.defer=true; g.src=u2+'f.js'; s.parentNode.insertBefore(g,s);
  })();
`;

type InsertFraudAlertParameters = {
  /** FraudAlertに指定するSessionId */
  sessionId: string;
  /** fraudalertのsiteId */
  siteId: string;
  /**
   * firstparty cookieのドメイン。
   *
   * マニュアルにあるけどこのプロジェクトでは多分意味ないと思う。
   */
  cookieDomain: string;
  /**
   * fraudAlertのAPIエンドポイント。
   *
   * 本番とstagingがある。ベンダーから提供されているマニュアルを参照すること。
   */
  fraudAlertEndpoint: string;
  /**
   * fraudAlertの静的モジュールが配置されてるオリジンを指定する。
   *
   * 本番とstagingがある。ベンダーから提供されているマニュアルを参照すること。
   */
  fraudAlertStaticContents: string;
  /** fraudAlertのスクリプト名。 */
  fraudAlertScriptName: string;
};

/**
 * fraud alertの初期化を行う。
 *
 * 戻り値は1秒経過するか、 https://static.fraud-alert.net/f.js が実行されたタイミングでresolveする。
 */
const insertFraudAlertScript = (parameters: InsertFraudAlertParameters): Promise<void> =>
  new Promise<void>((resolve) => {
    let fraudAlertScript: null | HTMLScriptElement = null;
    let timer = NaN;
    // CSPなどが原因で実行がうまくいかなくても1秒までしか待たない。
    const resolveHandler = () => {
      if (fraudAlertScript instanceof HTMLScriptElement) {
        fraudAlertScript.removeEventListener('load', resolveHandler);
      }
      window.clearTimeout(timer);
      resolve();
    };
    timer = window.setTimeout(resolveHandler, 1000);
    const script = FRAUD_ALERT_SCRIPT_TEMPLATE
      // JSON.stringifyすると、データががそのままquoteつきでsafeな文字列になるので、quoteごとreplaceする。
      .replaceAll(`'●●●●●●●●●●'`, JSON.stringify(parameters.sessionId))
      .replaceAll(`'.XXX.co.jp'`, JSON.stringify(parameters.cookieDomain))
      .replaceAll(`▲▲▲▲▲▲▲▲▲▲`, JSON.stringify(parameters.siteId))
      .replaceAll('"https://p.fraud-alert.net/"', JSON.stringify(parameters.fraudAlertEndpoint))
      .replaceAll('"https://static.fraud-alert.net/"', JSON.stringify(parameters.fraudAlertStaticContents))
      .replaceAll(`'f.js'`, JSON.stringify(parameters.fraudAlertScriptName));
    const scriptElement = document.createElement('script');
    scriptElement.async = true;
    // script.innerTextにスクリプトをセットせずblob urlにして与えることで、loadイベントが発生するようになる。
    // scriptのロードイベントは、elementに配置された後、scriptの同期処理部の評価が終了したタイミングで発生する。
    // `load`イベントの発生タイミングは `f.js` のscript要素が挿入され、かつ、 `f.js` のローディングが完了していないタイミングになることが保証される。
    const blob = new Blob([script], { type: 'text/javascript' });
    const url = URL.createObjectURL(blob);
    scriptElement.src = url;
    // blob urlでねじ込まれたscriptが実行されて直、このスクリプトが呼ばれる。
    // loadハンドラの実行は実行はf.jsよりも先んじる。そのため、f.jsの実行に対してイベントハンドラを仕掛けることが出来る。
    // f.jsがエラーを起こしていないことや、f.jsが呼び出すacceptの終了を待つことはできない。そこは多分、どうすることもできない。
    scriptElement.addEventListener('load', () => {
      document.head.removeChild(scriptElement);
      URL.revokeObjectURL(url);
      fraudAlertScript = document.querySelector(`script[src$="/${parameters.fraudAlertScriptName}"]`);
      if (fraudAlertScript) {
        fraudAlertScript.addEventListener('load', resolveHandler, { once: true });
      } else {
        // f.jsのうめこみに失敗している時。
        resolveHandler();
      }
    });
    document.head.appendChild(scriptElement);
  });

/**
 * fraud alertの初期化実行ステータス
 */
let execState: 'init' | 'loading' | 'completed' = 'init';

/**
 * fraud alertのスクリプト `f.js` が実行済みかどうか。
 *
 * 後続の処理を止めるわけには行かないため、CSPなどで実行が失敗しても1秒以内にtrueになる。
 */
const fraudAlertActivatedRef = ref(false);

/**
 * fraudAlertのjsが実行されたことを示す
 */
const fraudAlertActivatedReadOnlyRef = computed(() => fraudAlertActivatedRef.value);

type UseFraudAlertOptions = Omit<InsertFraudAlertParameters, 'sessionId'>;

/**
 * fraudAlertのscriptをロードするcomposable
 *
 * 引数のisActiveRefがtrueとなったタイミングで埋め込み処理がスタートします。
 * 複数箇所で `useFraudAlert` を同時に実行しても、処理は一つにまとまります。
 * @param isActiveRef
 * @returns fraudAlertのscriptの埋め込みが終わるとtrueになります。
 */
export function useFraudAlert(isActiveRef: Ref<boolean>, options: UnwrapRef<UseFraudAlertOptions>): Ref<boolean> {
  const sessionId = useSessionId();

  watch([isActiveRef] as const, async ([isActive]) => {
    if (isActive && execState === 'init') {
      execState = 'loading';
      await insertFraudAlertScript({ sessionId, ...options });
      execState = 'completed';
      fraudAlertActivatedRef.value = true;
    }
  });

  return fraudAlertActivatedReadOnlyRef;
}
